import React, { useContext, useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Slide,
  Alert,
} from '@mui/material';
import { motion } from 'framer-motion';
import Swal from 'sweetalert2';
import { FaCheck } from 'react-icons/fa';
import { ReportContext } from '../../ReportContext';
import SimpleTiptapEditor from '../../../components/tiptap-templates/simple/custom-editor';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const ApproveProposal = ({ solutionId, reload, setReload }) => {
  const { approveProposal } = useContext(ReportContext);
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const sessionKey = `approval_notes_${solutionId}`;

  // Load saved notes
  useEffect(() => {
    const saved = sessionStorage.getItem(sessionKey);
    if (saved) setNotes(saved);
  }, [sessionKey]);

  // Save notes as they're edited
  useEffect(() => {
    if (notes) {
      sessionStorage.setItem(sessionKey, notes);
    }
  }, [notes, sessionKey]);

  const handleOpen = async () => {
    const confirmResult = await Swal.fire({
      title: 'Confirm Approval',
      text: 'Are you sure you want to approve this proposal?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, continue',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#10b981',
      cancelButtonColor: '#6b7280',
    });

    if (confirmResult.isConfirmed) {
      setOpen(true);
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleSubmit = async () => {
    if (!notes.trim() || notes === '<p></p>') {
      Swal.fire({
        title: 'Error!',
        text: 'Approval notes cannot be empty',
        icon: 'error',
        confirmButtonColor: '#ef4444',
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await approveProposal({
        solution_id: solutionId,
        notes: notes,
      });

      if (response?.status === 'success') {
        sessionStorage.removeItem(sessionKey);
        setNotes('');
        setOpen(false);

        await Swal.fire({
          title: 'Approved!',
          text: 'Your approval has been submitted successfully.',
          icon: 'success',
          confirmButtonColor: '#10b981',
        });

        if (setReload) setReload(prev => !prev);
      } else {
        throw new Error(response?.message || 'Failed to submit approval');
      }
    } catch (error) {
      console.error('Approval error:', error);
      await Swal.fire({
        title: 'Error!',
        text: error.message || 'Failed to submit approval. Please try again.',
        icon: 'error',
        confirmButtonColor: '#ef4444',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditorChange = (htmlContent) => {
    setNotes(htmlContent);
  };

  return (
    <>
      <button
        className="btn btn-outline-success btn-sm"
        title="Approve"
        onClick={handleOpen}
        disabled={isSubmitting}
        style={{
          opacity: isSubmitting ? 0.6 : 1,
          cursor: isSubmitting ? 'not-allowed' : 'pointer',
        }}
      >
        <FaCheck />
        {isSubmitting && <span className="ms-1">...</span>}
      </button>

      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="md"
        TransitionComponent={Transition}
        PaperProps={{
          sx: {
            borderRadius: 3,
            zIndex: 1400,
            overflow: "hidden",
            maxHeight: "85vh",
            display: "flex",
            flexDirection: "column",
          },
          component: motion.div,
          initial: { opacity: 0, scale: 0.95 },
          animate: { opacity: 1, scale: 1 },
          exit: { opacity: 0, scale: 0.95 },
          transition: { duration: 0.3, ease: "easeOut" },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: "bold",
            background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
            color: "white",
            borderBottom: "1px solid rgba(255,255,255,0.1)",
            fontSize: "1.35rem",
            py: 2,
            px: 3,
          }}
        >
          Approval Notes
        </DialogTitle>

        <DialogContent
          sx={{
            mt: 2,
            px: 3,
            py: 2,
            overflowY: "auto",
            flex: 1,
          }}
        >
          <Typography
            variant="body1"
            sx={{
              mb: 2,
              fontWeight: 600,
              color: "#333",
              fontSize: "0.95rem",
            }}
          >
            Please enter your approval notes below:
          </Typography>

          <Box
            sx={{
              border: "1px solid #e0e0e0",
              borderRadius: 2,
              overflow: "hidden",
              background: "#fff",
              transition: "all 0.3s ease",
              "&:hover": {
                borderColor: "#10b981",
                boxShadow: "0 0 0 2px rgba(16, 185, 129, 0.1)",
              },
            }}
          >
            <SimpleTiptapEditor
              setContent={handleEditorChange}
              initialContent={notes || "<p>Enter your approval notes here...</p>"}
              navbarHeight={0}
              debounceDelay={300}
            />
          </Box>

          <Box sx={{ mt: 2 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
            >
              <span>💾</span> Your notes are saved automatically •
              <span>🖼️</span> You can add images if needed •
              <span>✨</span> Rich text formatting available
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: "1px solid #e0e0e0",
            justifyContent: "space-between",
            background: "#fafafa",
          }}
        >
          <Button
            onClick={handleClose}
            color="inherit"
            sx={{
              textTransform: "none",
              fontWeight: 500,
              "&:hover": {
                background: "#f5f5f5",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!notes.trim() || notes === "<p></p>" || notes === "<p>Enter your approval notes here...</p>" || isSubmitting}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 2,
              px: 4,
              py: 1,
              background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
              "&:hover": {
                background: "linear-gradient(135deg, #059669 0%, #047857 100%)",
              },
              "&.Mui-disabled": {
                background: "#d1d5db",
              },
            }}
          >
            {isSubmitting ? "Submitting..." : "Approve Proposal"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ApproveProposal;